import type { CreateElement, VNode } from 'vue';
import Vue from 'vue';
import { preventVueObservability } from '@cognitoforms/vuemodel';
import { Component, Prop, Inject } from 'vue-property-decorator';
import type { PaymentFieldStatus } from 'src/framework/payment/payment-types';
import type Log from 'src/framework/logging/log';
import type { HostedFieldsHandler, HostedFieldsSubmitResponse, PayPalHostedFieldsComponentOptions } from '@paypal/paypal-js/types/components/hosted-fields';
import type { PayPalNamespace } from '@paypal/paypal-js';
import { PaymentFormStyle } from './payment-form-style';

export type PayPalPaymentFormElements = {
	cardNumberElement: Element;
	cardExpiryElement: Element;
	cardCvvElement: Element;
	cardZipCodeElement: Element;
};

@Component
export default class CPayPalCard extends Vue {
	@Inject()
	log: Log;

	@Prop({ type: Function, default: null })
	createOrder: () => Promise<string>;

	@Prop()
	payPal: PayPalNamespace;

	@Prop({ type: PaymentFormStyle, required: true })
	inputStyles: PaymentFormStyle;

	@Prop({ type: Function, required: true })
	generateElementId: (idString: string) => string;

	@Prop()
	simulatePayment: boolean;

	elementStatus: PaymentFieldStatus = null;

	cardNumberElement: Element = null;

	cardExpiryElement: Element = null;

	cardCvvElement: Element = null;

	cardZipCodeElement: Element = null;

	hostedFieldsHandler: HostedFieldsHandler;

	mounted() {
		// Initialize the form immediately if already shown
		this.initializeForm();
	}

	beforeDestroy() {
		this.hostedFieldsHandler?.teardown();
	}

	render(h: CreateElement): VNode {
		let childNodes: VNode[];
		if (this.$scopedSlots && this.$scopedSlots.default) {
			// Render PayPal payment form with provided slot content
			childNodes = this.$scopedSlots.default({});
		}
		else {
			// Render PayPal payment form with default markup
			childNodes = [
				h('div', { attrs: { id: this.generateElementId('cog-card-number') } }),
				h('div', { attrs: { id: this.generateElementId('cog-card-expiry') } }),
				h('div', { attrs: { id: this.generateElementId('cog-card-cvv') } }),
				h('div', { attrs: { id: this.generateElementId('cog-zip-code') } })
			];
		}
		return h('div', {}, childNodes);
	}

	async initializeForm() {
		const targetElements: PayPalPaymentFormElements = {
			cardNumberElement: this.$el.querySelector(this.generateElementId('#cog-card-number')),
			cardExpiryElement: this.$el.querySelector(this.generateElementId('#cog-card-expiry')),
			cardCvvElement: this.$el.querySelector(this.generateElementId('#cog-card-cvv')),
			cardZipCodeElement: this.$el.querySelector(this.generateElementId('#cog-zip-code'))
		};

		this.cardNumberElement = targetElements.cardNumberElement;
		this.cardExpiryElement = targetElements.cardExpiryElement;
		this.cardCvvElement = targetElements.cardCvvElement;
		this.cardZipCodeElement = targetElements.cardZipCodeElement;

		// Prevent Vue observability, since otherwise the CC field's iframe may attempt to access properties that were defined in this frame by Vue, resulting in errors
		const hostedFieldOptions: PayPalHostedFieldsComponentOptions = {
			createOrder: this.createOrder,
			fields: {
				number: {
					selector: this.generateElementId('#cog-card-number'),
					placeholder: this.cardNumberElement.getAttribute('data-cognito-placeholder') || 'Credit Card Number'
				},
				expirationDate: {
					selector: this.generateElementId('#cog-card-expiry'),
					placeholder: this.cardExpiryElement.getAttribute('data-cognito-placeholder') || 'MM/YY'
				},
				cvv: {
					selector: this.generateElementId('#cog-card-cvv'),
					placeholder: this.cardCvvElement.getAttribute('data-cognito-placeholder') || 'CVV'
				},
				postalCode: {
					selector: this.generateElementId('#cog-zip-code'),
					placeholder: this.cardZipCodeElement.getAttribute('data-cognito-placeholder') || 'Zip/Postal Code'
				}
			},
			onError(err) {
				console.warn(err);
			},
			styles: this.inputStyles.useFormTheme ? {
				'input': {
					'line-height': this.inputStyles.lineHeight,
					'font-family': this.inputStyles.fontFamily,
					'font-size': this.inputStyles.fontSize,
					'font-weight': this.inputStyles.fontWeight
				},
				'::placeholder': {
					'color': this.inputStyles.placeholderColor
				}
			} : {
				'input': {
					'line-height': '13px',
					'font-family': 'system-ui, -apple-system, blinkmacsystemfont, "Segoe UI", roboto, oxygen-sans, ubuntu, cantarell, "Helvetica Neue", arial, sans-serif',
					'font-size': '14px'
				},
				'::placeholder': {
					'color': '#92b8b8'
				}
			}
		};

		this.payPal.HostedFields.render(hostedFieldOptions).then(handler => {
			this.$el.querySelector(this.generateElementId('#cog-card-number') + ' .cog-input__placeholder').remove();
			this.$el.querySelector(this.generateElementId('#cog-card-expiry') + ' .cog-input__placeholder').remove();
			this.$el.querySelector(this.generateElementId('#cog-card-cvv') + ' .cog-input__placeholder').remove();
			this.$el.querySelector(this.generateElementId('#cog-zip-code') + ' .cog-input__placeholder').remove();

			// disable card fields if simulating payment
			if (this.simulatePayment) {
				handler.setAttribute({ field: 'number', attribute: 'disabled', value: 'true' });
				handler.setAttribute({ field: 'expirationDate', attribute: 'disabled', value: 'true' });
				handler.setAttribute({ field: 'cvv', attribute: 'disabled', value: 'true' });
				handler.setAttribute({ field: 'postalCode', attribute: 'disabled', value: 'true' });
			}

			preventVueObservability(handler);
			this.hostedFieldsHandler = handler;
		});

		this.$emit('ready');
	}

	clear() {
		this.hostedFieldsHandler.clear('number');
		this.hostedFieldsHandler.clear('expirationDate');
		this.hostedFieldsHandler.clear('cvv');
		this.hostedFieldsHandler.clear('postalCode');
	}

	async tokenize(): Promise<HostedFieldsSubmitResponse> {
		const response = await this.hostedFieldsHandler.submit({ contingencies: ['SCA_WHEN_REQUIRED'] });
		return response;
	}
}